import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BillingService, Currency } from '@galaxy/billing';
import { AppPrice, AppPricingService, FieldMask } from '@galaxy/marketplace-apps';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OrderFulfillmentService } from '@vendasta/order-fulfillment';
import { Subscription, combineLatest, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

export interface RequestSpendChangeDialogDataInterface {
  productId: string;
  productName: string;
  billingId: string;
  billingFrequency: string;
  activationId: string;
  businessId: string;
  partnerId: string;
}

@Component({
  templateUrl: 'request-spend-change-dialog.component.html',
  styleUrls: ['request-spend-change-dialog.component.scss'],
})
export class RequestSpendChangeDialogComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  isLoading = true;
  spendChangeSent = false;
  appPricing: AppPrice;
  currency: Currency;
  newSpendAmount = 0;
  minSpendAmount = 0;
  requesterNote: string;
  currentSpendAmount: number;

  constructor(
    public dialogRef: MatDialogRef<RequestSpendChangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RequestSpendChangeDialogDataInterface,
    private orderFulfillmentService: OrderFulfillmentService,
    private snackbarService: SnackbarService,
    private appPricingService: AppPricingService,
    private billingService: BillingService,
  ) {}

  ngOnInit(): void {
    const partnerCurrency$ = this.billingService
      .getMultiContract([this.data.partnerId])
      .pipe(map((contracts) => contracts[this.data.partnerId].currency));

    const appPricing$ = this.appPricingService
      .getMultiPricing([this.data.productId], new FieldMask({ paths: ['wholesale'] }), this.data.partnerId, '')
      .pipe(
        map((appPrices) => {
          return appPrices?.[0]?.pricesForContexts?.['wholesale'];
        }),
      );

    const currentSpend$ = this.orderFulfillmentService
      .GetCurrentSpend(this.data.partnerId, this.data.businessId, this.data.productId, this.data.activationId)
      .pipe(
        map((resp) => resp.spendAmount),
        catchError((err) => {
          console.log('Failed to get current spend:', err);
          this.snackbarService.openErrorSnack('Failed to get current spend.');
          return of(0);
        }),
      );

    this.subscriptions.push(
      combineLatest([appPricing$, partnerCurrency$, currentSpend$]).subscribe(
        ([appPricing, currency, currentSpend]) => {
          this.appPricing = appPricing;
          this.newSpendAmount =
            appPricing?.pricesForEditions?.['']?.pricesForCurrencies?.[
              currency
            ]?.pricesForFrequencies?.[0]?.pricingRules?.[0]?.price;
          this.currency = currency;
          this.currentSpendAmount = currentSpend;
          this.isLoading = false;
        },
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  changeSpendAmount(newSpend: number): void {
    this.newSpendAmount = newSpend;
  }

  changeRequesterNote(newNote: string): void {
    this.requesterNote = newNote;
  }

  requestSpendChange(): void {
    this.spendChangeSent = true;
    const newSpendAmount = this.newSpendAmount;
    const requesterNote = this.requesterNote;

    this.subscriptions.push(
      this.orderFulfillmentService
        .changeSpendRequest(
          this.data.businessId,
          this.data.productId,
          this.data.activationId,
          newSpendAmount,
          requesterNote,
        )
        .pipe(first())
        .subscribe(
          (res) => {
            if (res) {
              this.snackbarService.successSnack('Your request has been sent');
              this.dialogRef.close();
            } else {
              this.spendChangeSent = false;
              this.snackbarService.errorSnack('An unexpected error occurred. Please try again');
            }
          },
          (err) => {
            this.spendChangeSent = false;
            this.snackbarService.errorSnack(err.error.message);
          },
        ),
    );
  }
}
